/**
 * LearningCenter — capítulos de aprendizaje (Envío AP-1).
 *
 * Dos modos con el mismo reproductor:
 *   · onboarding → al iniciar: vista previa del capítulo destacado + lista,
 *                  «Saltear por ahora» y «No volver a mostrar al iniciar».
 *   · library    → ? › Videos instructivos: biblioteca en tarjetas con
 *                  filtro por tema; al tocar uno se reproduce acá mismo.
 *
 * No decide cuándo abrirse (eso es LearningGate): sólo muestra y avisa.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, Play, X } from 'lucide-react';
import {
  chapterLabel, filterByTopic, formatDuration, isPlayable, onboardingChapters,
  chapterThumbnailCandidates, pickFeatured, sortChapters, topicsOf, youtubeVideoId,
} from './learningModel';
import { loadYouTubeApi } from './youtubePlayerApi';
import './learning.css';

const SEEN_RATIO = 0.6;

export const YouTubeThumbnail = ({ chapter, alt = '', className, fallback = null }) => {
  const candidates = chapterThumbnailCandidates(chapter);
  const [index, setIndex] = useState(0);
  useEffect(() => { setIndex(0); }, [chapter?.id, chapter?.thumb_url, chapter?.video_url]);
  if (!candidates[index]) return fallback;
  return <img src={candidates[index]} alt={alt} className={className} loading="lazy"
              onError={() => setIndex((current) => current + 1)} />;
};

export const YouTubePlayer = ({ chapter, autoPlay, onSeen }) => {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const reported = useRef(false);
  const onSeenRef = useRef(onSeen);
  const chapterIdRef = useRef(chapter.id);
  const autoPlayRef = useRef(autoPlay);
  const [unavailable, setUnavailable] = useState(false);
  const videoId = chapter.youtube_video_id || youtubeVideoId(chapter.video_url);

  onSeenRef.current = onSeen;
  chapterIdRef.current = chapter.id;
  autoPlayRef.current = autoPlay;

  useEffect(() => {
    let active = true;
    let timer;
    reported.current = false;
    setUnavailable(false);
    loadYouTubeApi().then((YT) => {
      if (!active || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId,
        playerVars: { autoplay: autoPlay ? 1 : 0, playsinline: 1, rel: 0 },
        events: {
          onReady: (event) => {
            if (autoPlayRef.current) event.target.playVideo();
            timer = window.setInterval(() => {
              const duration = event.target.getDuration?.() || 0;
              const current = event.target.getCurrentTime?.() || 0;
              if (!reported.current && duration && current / duration >= SEEN_RATIO) {
                reported.current = true;
                onSeenRef.current?.(chapterIdRef.current);
              }
            }, 1000);
          },
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.ENDED && !reported.current) {
              reported.current = true;
              onSeenRef.current?.(chapterIdRef.current);
            }
          },
          onError: () => active && setUnavailable(true),
        },
      });
    }).catch(() => active && setUnavailable(true));
    return () => {
      active = false;
      if (timer) window.clearInterval(timer);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [videoId]);

  useEffect(() => {
    if (autoPlay) playerRef.current?.playVideo?.();
  }, [autoPlay]);

  if (unavailable || !videoId) return (
    <div className="sml-learn-youtube-fallback">
      <p>Este video no está disponible para reproducirse aquí.</p>
      <a href={chapter.video_url} target="_blank" rel="noopener noreferrer">Abrir en YouTube</a>
    </div>
  );
  return <div className="sml-learn-youtube" ref={hostRef} data-testid="learning-youtube-player" />;
};

export const Player = ({ chapter, autoPlay, onSeen }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const reported = useRef(false);

  useEffect(() => {
    reported.current = false;
    setPlaying(false);
    if (autoPlay && videoRef.current) {
      videoRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
  }, [chapter?.id, autoPlay]);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().then(() => setPlaying(true)).catch(() => {});
  };
  const report = () => {
    if (reported.current || !chapter) return;
    reported.current = true;
    onSeen?.(chapter.id);
  };
  const onTime = (e) => {
    const v = e.currentTarget;
    if (v.duration && v.currentTime / v.duration >= SEEN_RATIO) report();
  };

  if (!chapter) return null;
  if (chapter.video_source === 'youtube') return (
    <div className="sml-learn-player" data-testid="learning-player">
      <YouTubePlayer chapter={chapter} autoPlay={autoPlay} onSeen={onSeen} />
      <span className="sml-learn-tag">
        {chapterLabel(chapter).toUpperCase()} · {formatDuration(chapter.duration_seconds)}
      </span>
    </div>
  );
  return (
    <div className="sml-learn-player" data-testid="learning-player">
      <video
        ref={videoRef}
        src={chapter.video_url}
        poster={chapter.thumb_url || undefined}
        controls={playing}
        playsInline
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onTimeUpdate={onTime}
        onEnded={report}
        aria-label={`${chapterLabel(chapter)} · ${chapter.title}`}
      />
      {!playing && (
        <button type="button" className="sml-learn-play" onClick={play}
                aria-label={`Reproducir ${chapterLabel(chapter)}`} data-testid="learning-play">
          <Play size={30} fill="currentColor" aria-hidden="true" />
        </button>
      )}
      <span className="sml-learn-tag">
        {chapterLabel(chapter).toUpperCase()} · {formatDuration(chapter.duration_seconds)}
      </span>
    </div>
  );
};

const ChapterRow = ({ chapter, active, seen, onPick }) => {
  const ready = isPlayable(chapter);
  return (
    <button
      type="button"
      className={`sml-learn-row ${active ? 'is-active' : ''}`}
      onClick={() => ready && onPick(chapter)}
      disabled={!ready}
      aria-current={active ? 'true' : undefined}
      data-testid={`learning-row-${chapter.number}`}
    >
      <span className={`sml-learn-num ${ready ? 'is-ready' : ''}`}>
        {String(chapter.number).padStart(2, '0')}
      </span>
      <span className="sml-learn-row-text">
        <span className="sml-learn-row-title">{chapter.title}</span>
        <span className="sml-learn-row-meta">
          {ready ? `${formatDuration(chapter.duration_seconds)} · ${seen ? 'Visto' : 'Disponible'}` : 'Próximamente'}
        </span>
      </span>
      {seen && <Check size={16} className="sml-learn-seen" aria-label="Visto" />}
    </button>
  );
};

const Card = ({ chapter, seen, onPick }) => {
  const ready = isPlayable(chapter);
  return (
    <article className="sml-learn-card">
      <button type="button" className="sml-learn-card-thumb" onClick={() => ready && onPick(chapter)}
              disabled={!ready} aria-label={ready ? `Ver ${chapter.title}` : `${chapter.title}: próximamente`}
              data-testid={`learning-card-${chapter.number}`}>
        {ready
          ? <YouTubeThumbnail chapter={chapter}
              fallback={<span className="sml-learn-card-num">{String(chapter.number).padStart(2, '0')}</span>} />
          : <span className="sml-learn-card-num">{String(chapter.number).padStart(2, '0')}</span>}
        <span className={`sml-learn-badge ${seen ? 'is-seen' : ready ? 'is-new' : ''}`}>
          {seen ? 'VISTO' : ready ? formatDuration(chapter.duration_seconds) : 'PRÓXIMAMENTE'}
        </span>
        {seen && <span className="sml-learn-progress" aria-hidden="true" />}
        {ready && <span className="sml-learn-card-play" aria-hidden="true"><Play size={18} fill="currentColor" /></span>}
      </button>
      <div className="sml-learn-card-text">
        <span className="sml-learn-card-kicker">{chapterLabel(chapter).toUpperCase()} · {(chapter.topic || '').toUpperCase()}</span>
        <h3>{chapter.title}</h3>
        {chapter.description && ready && <p>{chapter.description}</p>}
      </div>
    </article>
  );
};

export const LearningCenter = ({
  open, mode = 'library', data, initialChapterId, onClose, onSeen, onHideChange,
}) => {
  const all = useMemo(() => sortChapters(data?.chapters || []), [data]);
  const list = mode === 'onboarding' ? onboardingChapters(data) : all;
  const seen = useMemo(() => new Set(data?.state?.seen || []), [data]);
  const [current, setCurrent] = useState(null);
  const [autoPlay, setAutoPlay] = useState(false);
  const [topic, setTopic] = useState('Todos');
  const [dontShow, setDontShow] = useState(!!data?.state?.hidden);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const wanted = all.find((c) => c.id === initialChapterId && isPlayable(c));
    setCurrent(wanted || (mode === 'onboarding' ? pickFeatured(list) : null));
    setAutoPlay(!!wanted);
    setTopic('Todos');
    setDontShow(!!data?.state?.hidden);
    setTimeout(() => closeRef.current?.focus(), 50);
  }, [open, mode, initialChapterId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.('skip'); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const pick = (chapter) => { setCurrent(chapter); setAutoPlay(true); };
  const toggleDontShow = () => {
    const next = !dontShow;
    setDontShow(next);
    onHideChange?.(next);
  };

  // ── Onboarding ─────────────────────────────────────────────
  if (mode === 'onboarding') {
    return (
      <div className="sml-learn-overlay" data-testid="learning-onboarding">
        <section className="sml-learn-modal" role="dialog" aria-modal="true" aria-labelledby="sml-learn-title"
                 ref={closeRef} tabIndex={-1}>
          <header className="sml-learn-head">
            <div>
              <div className="sml-learn-kicker">BIENVENIDA</div>
              <h2 id="sml-learn-title">Aprendé Studio Media Labs en capítulos cortos</h2>
              <p>Enrik te muestra cómo crear, editar y publicar. Podés verlos ahora o cuando quieras desde <b>? › Videos instructivos</b>.</p>
            </div>
            <button type="button" className="sml-learn-x" onClick={() => onClose?.('skip')}
                    aria-label="Cerrar" data-testid="learning-close"><X size={18} /></button>
          </header>

          <div className="sml-learn-body">
            <div className="sml-learn-main">
              <Player chapter={current} autoPlay={autoPlay} onSeen={onSeen} />
              {current && (
                <div className="sml-learn-info">
                  <h3>{current.title}</h3>
                  {current.description && <p>{current.description}</p>}
                </div>
              )}
            </div>
            <nav className="sml-learn-list" aria-label="Capítulos">
              <div className="sml-learn-list-head">CAPÍTULOS</div>
              {list.map((c) => (
                <ChapterRow key={c.id} chapter={c} active={current?.id === c.id}
                            seen={seen.has(c.id)} onPick={pick} />
              ))}
            </nav>
          </div>

          <footer className="sml-learn-foot">
            <label className="sml-learn-check">
              <input type="checkbox" checked={dontShow} onChange={toggleDontShow}
                     data-testid="learning-dont-show" />
              No volver a mostrar al iniciar
            </label>
            <div className="sml-learn-actions">
              <button type="button" className="sml-learn-ghost" onClick={() => onClose?.('skip')}
                      data-testid="learning-skip">Saltear por ahora</button>
              {current && (
                <button type="button" className="sml-learn-primary" onClick={() => pick(current)}
                        data-testid="learning-watch">
                  <Play size={15} fill="currentColor" aria-hidden="true" /> Ver {chapterLabel(current).toLowerCase()}
                </button>
              )}
            </div>
          </footer>
        </section>
      </div>
    );
  }

  // ── Biblioteca ─────────────────────────────────────────────
  const topics = ['Todos', ...topicsOf(all)];
  const visible = filterByTopic(all, topic);
  return (
    <div className="sml-learn-overlay" onClick={() => onClose?.('close')} data-testid="learning-library">
      <section className="sml-learn-modal is-library" role="dialog" aria-modal="true"
               aria-labelledby="sml-learn-lib" onClick={(e) => e.stopPropagation()}
               ref={closeRef} tabIndex={-1}>
        <header className="sml-learn-head">
          <div>
            {current && (
              <button type="button" className="sml-learn-back" onClick={() => setCurrent(null)}
                      data-testid="learning-back">
                <ArrowLeft size={15} aria-hidden="true" /> Volver a la biblioteca
              </button>
            )}
            <h2 id="sml-learn-lib">{current ? current.title : 'Videos instructivos'}</h2>
            {!current && <p>Capítulos cortos para dominar la plataforma.</p>}
          </div>
          <button type="button" className="sml-learn-x" onClick={() => onClose?.('close')}
                  aria-label="Cerrar" data-testid="learning-close"><X size={18} /></button>
        </header>

        {current ? (
          <div className="sml-learn-body is-single">
            <div className="sml-learn-main">
              <Player chapter={current} autoPlay={autoPlay} onSeen={onSeen} />
              {current.description && <div className="sml-learn-info"><p>{current.description}</p></div>}
            </div>
          </div>
        ) : (
          <>
            {topics.length > 2 && (
              <div className="sml-learn-tabs" role="tablist" aria-label="Temas">
                {topics.map((t) => (
                  <button key={t} type="button" role="tab" aria-selected={topic === t}
                          className={topic === t ? 'is-on' : ''} onClick={() => setTopic(t)}>{t}</button>
                ))}
              </div>
            )}
            <div className="sml-learn-grid">
              {visible.map((c) => <Card key={c.id} chapter={c} seen={seen.has(c.id)} onPick={pick} />)}
              {visible.length === 0 && <p className="sml-learn-empty">Todavía no hay capítulos en este tema.</p>}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default LearningCenter;
